import { motion } from "framer-motion";
import { ArrowDown, Instagram, Music2 } from "lucide-react";
import { IMAGES } from "@/lib/content";
import { PrimaryButton, GhostButton } from "@/components/site/ui";
import { scrollToId } from "@/components/sections/utils";

export function Hero() {
  return (
    <section id="home" className="relative isolate min-h-[100svh] flex items-center overflow-hidden">
      <div className="absolute inset-0 -z-20">
        <img src={IMAGES.sunsetTrack} alt="Pink sunset sky fading into a running track" className="h-full w-full object-cover object-center" />
        <img src={IMAGES.cutout} alt="" aria-hidden data-testid="hero-mobile-bg" className="lg:hidden absolute bottom-0 right-0 h-[66%] object-contain object-bottom opacity-95 [mask-image:linear-gradient(to_top,rgba(0,0,0,1)_88%,rgba(0,0,0,0)_100%)] [-webkit-mask-image:linear-gradient(to_top,rgba(0,0,0,1)_88%,rgba(0,0,0,0)_100%)]" />
        <div className="absolute inset-0 bg-gradient-to-r from-ivory via-ivory/65 to-ivory/0" />
        <div className="absolute inset-0 bg-gradient-to-b from-ivory/40 via-transparent to-ivory/30" />
        <div className="lg:hidden absolute inset-0 bg-gradient-to-t from-ivory/55 via-transparent to-ivory/30" />
      </div>
      <motion.div
        aria-hidden
        animate={{ y: [0, -18, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/4 right-1/3 -z-10 h-80 w-80 rounded-full bg-blush/30 blur-3xl"
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid lg:grid-cols-[1.05fr_0.95fr] gap-8 items-center pt-28 pb-16">
        <div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="flex items-center gap-4">
            <span className="h-px w-12 bg-bronze" />
            <span className="label-eyebrow">Professional Athlete</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="mt-5 font-heading text-6xl sm:text-7xl lg:text-[7rem] leading-[0.88] tracking-tight text-ink"
          >
            LEAH<br /><span className="text-bronze">THAMES</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }} className="mt-6 font-body text-sm md:text-base uppercase tracking-[0.22em] text-ink/75">
            Professional 400m &amp; 200m Sprinter &amp; Relay Athlete
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.32 }} className="mt-3 font-heading text-2xl md:text-3xl tracking-[0.14em] text-ink">
            FAST. FEMININE. FOCUSED.
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }} className="mt-4 font-accent text-2xl md:text-3xl italic text-ink/70 max-w-md leading-snug">
            Building an elite career with confidence, purpose, and a little bit of pink.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }} className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
            <a href="https://instagram.com/leleskye_" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-ink/70 hover:text-bronze transition-colors">
              <Instagram size={16} /> <span className="font-body text-xs uppercase tracking-wide">@leleskye_</span>
            </a>
            <a href="https://tiktok.com/@leleskye" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-ink/70 hover:text-bronze transition-colors">
              <Music2 size={16} /> <span className="font-body text-xs uppercase tracking-wide">@leleskye</span>
            </a>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }} className="mt-8 flex flex-wrap gap-3">
            <PrimaryButton onClick={() => scrollToId("partnerships")} testid="hero-partner-btn">Partner With Leah</PrimaryButton>
            <GhostButton onClick={() => scrollToId("journey")} testid="hero-journey-btn">Follow The Journey</GhostButton>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.3 }} className="relative hidden lg:flex justify-center items-end h-[82vh]">
          <div className="absolute bottom-10 h-72 w-72 rounded-full bg-blush/40 blur-3xl" />
          <img src={IMAGES.cutout} alt="Leah Thames" className="relative z-10 max-h-[82vh] object-contain drop-shadow-2xl" />
          <div className="absolute bottom-16 left-2 z-20 rounded-2xl bg-ivory/90 backdrop-blur px-5 py-3 shadow-lg border border-champagne/30">
            <p className="font-heading text-2xl text-bronze">51.54</p>
            <p className="label-eyebrow">400m PB</p>
          </div>
        </motion.div>
      </div>

      <button onClick={() => scrollToId("about")} data-testid="hero-scroll-indicator" className="absolute bottom-8 left-1/2 -translate-x-1/2 text-bronze animate-float-slow">
        <ArrowDown size={26} />
      </button>
    </section>
  );
}
